import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

import type {
  CreateStonePriceRuleRequest,
  UpdateStonePriceRuleRequest,
} from '@nucleus-commerce/api-client/admin';

import { stonePricingApi } from '../api/stone-pricing-api';

import { dictionaryKeys } from './dictionary-keys';

/**
 * Bands decide which stones a setting can sell, and each product's stone setting reports how many
 * bands apply and how many stones stay unpriced; those queries carry `configurator` in their key.
 */
function refresh(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: dictionaryKeys.stonePriceRules() }),
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey.includes('configurator'),
    }),
  ]);
}

/** 422 on `body.rangeFrom` (overlapping band) or `body.marginPct` is shown on the dialog's fields. */
export function useCreateStonePriceRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateStonePriceRuleRequest) => stonePricingApi.create(body),
    meta: { successMessage: 'dictionary:stonePricing.toasts.created', silentError: true },
    onSuccess: () => refresh(queryClient),
  });
}

export function useUpdateStonePriceRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateStonePriceRuleRequest }) =>
      stonePricingApi.update(id, body),
    meta: { successMessage: 'dictionary:stonePricing.toasts.updated', silentError: true },
    onSuccess: () => refresh(queryClient),
  });
}

export function useDeleteStonePriceRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => stonePricingApi.remove(id),
    meta: { successMessage: 'dictionary:stonePricing.toasts.deleted', silentError: true },
    onSuccess: () => refresh(queryClient),
  });
}
